import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { examApi, classesApi, subjectsApi } from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, FileCheck, Loader2, BookOpen, Save, Send, Wand2,
} from 'lucide-react';
import AIGeneratePaperDialog from './AIGeneratePaperDialog';

const EXAM_TYPES = ['Unit Test', 'Periodic Test', 'Mid-Term', 'Pre-Board', 'Annual Exam'];

const CreatePaperPage = () => {
  const navigate = useNavigate();
  const { paperId } = useParams();  // optional - if set, we're editing a draft
  const isEdit = Boolean(paperId);

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [bank, setBank] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);

  const [meta, setMeta] = useState({
    title: '',
    academic_year: '2026-27',
    class_id: '',
    subject_id: '',
    exam_type: 'Unit Test',
    total_marks: 50,
    duration_minutes: 90,
    internal_choice: false,
    general_instructions: 'All questions are compulsory.\nMarks for each question are indicated against it.\nWrite answers in your own words.',
    release_at: '',
  });

  const [sections, setSections] = useState([
    { name: 'Section A', instruction: '', marks_per_question: 1, question_ids: [] },
  ]);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Picker state per-section
  const [pickerOpen, setPickerOpen] = useState(null);  // section index
  const [pickerFilter, setPickerFilter] = useState({ type: 'all', difficulty: 'all', search: '' });

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([classesApi.getAll(), subjectsApi.getAll()]);
      setClasses(c.data.classes || []);
      setSubjects(s.data.subjects || []);
    })();
  }, []);

  // Hydrate from existing draft if editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await examApi.getPaper(paperId);
        setMeta({
          title: data.title || '',
          academic_year: data.academic_year || '2026-27',
          class_id: data.class_id || '',
          subject_id: data.subject_id || '',
          exam_type: data.exam_type || 'Unit Test',
          total_marks: data.total_marks || 0,
          duration_minutes: data.duration_minutes || 60,
          internal_choice: !!data.internal_choice,
          general_instructions: data.general_instructions || '',
          release_at: data.release_at || '',
        });
        setSections((data.sections || []).map(s => ({
          name: s.name, instruction: s.instruction || '',
          marks_per_question: s.marks_per_question, question_ids: s.question_ids || [],
        })));
      } catch (e) {
        toast.error('Could not load paper for editing');
        navigate('/examinations/my-papers');
      }
    })();
  }, [paperId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load question bank for current class+subject
  useEffect(() => {
    if (!meta.class_id || !meta.subject_id) {
      setBank([]);
      return;
    }
    (async () => {
      setBankLoading(true);
      try {
        const { data } = await examApi.listQuestions({
          class_id: meta.class_id,
          subject_id: meta.subject_id,
        });
        setBank(data.questions || []);
      } finally {
        setBankLoading(false);
      }
    })();
  }, [meta.class_id, meta.subject_id]);

  const bankById = useMemo(() => {
    const m = {};
    bank.forEach(q => { m[q.id] = q; });
    return m;
  }, [bank]);

  const addSection = () => {
    setSections(s => [
      ...s,
      { name: `Section ${String.fromCharCode(65 + s.length)}`, instruction: '', marks_per_question: 2, question_ids: [] },
    ]);
  };

  const removeSection = (i) => {
    setSections(s => s.filter((_, idx) => idx !== i));
  };

  const updateSection = (i, patch) => {
    setSections(s => s.map((sec, idx) => idx === i ? { ...sec, ...patch } : sec));
  };

  const toggleQuestion = (secIndex, qid) => {
    setSections(s => s.map((sec, i) => {
      if (i !== secIndex) return sec;
      const has = sec.question_ids.includes(qid);
      return { ...sec, question_ids: has
        ? sec.question_ids.filter(x => x !== qid)
        : [...sec.question_ids, qid] };
    }));
  };

  const computedMarks = useMemo(() => sections.reduce(
    (sum, s) => sum + (s.question_ids.length * Number(s.marks_per_question || 0)), 0,
  ), [sections]);

  const validate = () => {
    if (!meta.class_id || !meta.subject_id) return 'Choose class and subject';
    if (!meta.exam_type) return 'Choose exam type';
    if (sections.length === 0) return 'Add at least one section';
    if (sections.some(s => s.question_ids.length === 0)) return 'Each section must have at least one question';
    return null;
  };

  const save = async (submit = false) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    const payload = {
      ...meta,
      total_marks: computedMarks || Number(meta.total_marks),
      duration_minutes: Number(meta.duration_minutes),
      sections: sections.map(s => ({
        name: s.name,
        instruction: s.instruction,
        marks_per_question: Number(s.marks_per_question),
        question_ids: s.question_ids,
      })),
      release_at: meta.release_at || null,
    };
    setSaving(true);
    try {
      let pid = paperId;
      if (isEdit) {
        await examApi.updatePaper(paperId, payload);
      } else {
        const { data } = await examApi.createPaper(payload);
        pid = data.id;
      }
      if (submit) {
        await examApi.submitPaper(pid);
        toast.success('Paper submitted for approval');
      } else {
        toast.success(isEdit ? 'Draft updated' : 'Draft saved');
      }
      navigate('/examinations/my-papers');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const visibleBank = useMemo(() => {
    return bank.filter(q => {
      if (pickerFilter.type !== 'all' && q.question_type !== pickerFilter.type) return false;
      if (pickerFilter.difficulty !== 'all' && q.difficulty !== pickerFilter.difficulty) return false;
      if (pickerFilter.search) {
        const s = pickerFilter.search.toLowerCase();
        if (!q.question_text?.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [bank, pickerFilter]);

  // AI generated a full paper — save the embedded questions to the bank,
  // then load them into the form's sections.
  const handleAiPaper = async (aiPaper) => {
    try {
      const allNew = [];
      const sectionPlans = [];
      for (const sec of aiPaper.sections || []) {
        const start = allNew.length;
        for (const q of sec.questions || []) {
          allNew.push({
            class_id: form.class_id || aiPaper.class_id || meta.class_id,
            subject_id: form.subject_id || aiPaper.subject_id || meta.subject_id,
            chapter: q.chapter || '',
            topic: q.topic || '',
            question_type: q.question_type,
            question_text: q.question_text,
            marks: Number(q.marks) || Number(sec.marks_per_question) || 1,
            difficulty: q.difficulty || 'Medium',
            learning_outcome: q.learning_outcome || '',
            competency_tag: q.competency_tag || '',
            options: q.options || null,
            correct_answer: q.correct_answer || '',
            explanation: q.explanation || '',
          });
        }
        sectionPlans.push({
          name: sec.name,
          instruction: sec.instruction || '',
          marks_per_question: Number(sec.marks_per_question) || 1,
          countRange: [start, allNew.length],
        });
      }
      // Strip class_id/subject_id from "form" reference and use meta
      allNew.forEach(q => {
        q.class_id = meta.class_id || aiPaper.class_id;
        q.subject_id = meta.subject_id || aiPaper.subject_id;
      });
      const { data } = await examApi.aiSaveQuestions({ questions: allNew });
      const ids = data.ids || [];
      // Map ids back into sections
      const newSections = sectionPlans.map(sp => ({
        name: sp.name,
        instruction: sp.instruction,
        marks_per_question: sp.marks_per_question,
        question_ids: ids.slice(sp.countRange[0], sp.countRange[1]),
      }));
      setSections(newSections);
      setMeta(m => ({
        ...m,
        title: aiPaper.title || m.title,
        general_instructions: aiPaper.general_instructions || m.general_instructions,
        class_id: m.class_id || aiPaper.class_id,
        subject_id: m.subject_id || aiPaper.subject_id,
      }));
      toast.success(`Loaded AI paper: ${ids.length} questions added to bank + paper draft populated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply AI paper');
    }
  };

  const filteredSubjects = useMemo(() => {
    if (!meta.class_id) return subjects;
    return subjects.filter(s => s.class_id === meta.class_id);
  }, [subjects, meta.class_id]);

  return (
    <div className="space-y-6" data-testid="create-paper-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/examinations/my-papers')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">
            {isEdit ? 'Edit Draft Paper' : 'Create Question Paper'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-base">
            Computed Marks: <span className="ml-1 font-bold">{computedMarks}</span>
          </Badge>
          <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-paper-open-btn">
            <Wand2 className="h-4 w-4 mr-2 text-primary" /> Generate with AI
          </Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Draft
          </Button>
          <Button onClick={() => save(true)} disabled={saving} data-testid="submit-paper-btn">
            <Send className="h-4 w-4 mr-2" /> Submit for Approval
          </Button>
        </div>
      </div>

      {/* Paper Meta */}
      <Card>
        <CardHeader><CardTitle>Paper Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Paper Title</Label>
            <Input placeholder="e.g. Mathematics — Unit Test 1"
                   value={meta.title}
                   onChange={e => setMeta(m => ({ ...m, title: e.target.value }))}
                   data-testid="paper-title" />
          </div>
          <div>
            <Label>Academic Year</Label>
            <Input value={meta.academic_year}
                   onChange={e => setMeta(m => ({ ...m, academic_year: e.target.value }))}
                   data-testid="paper-year" />
          </div>
          <div>
            <Label>Class *</Label>
            <Select value={meta.class_id} onValueChange={(v) => setMeta(m => ({ ...m, class_id: v, subject_id: '' }))}>
              <SelectTrigger data-testid="paper-class"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subject *</Label>
            <Select value={meta.subject_id} onValueChange={(v) => setMeta(m => ({ ...m, subject_id: v }))}>
              <SelectTrigger data-testid="paper-subject"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Exam Type *</Label>
            <Select value={meta.exam_type} onValueChange={(v) => setMeta(m => ({ ...m, exam_type: v }))}>
              <SelectTrigger data-testid="paper-examtype"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXAM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duration (mins)</Label>
            <Input type="number" value={meta.duration_minutes}
                   onChange={e => setMeta(m => ({ ...m, duration_minutes: e.target.value }))}
                   data-testid="paper-duration" />
          </div>
          <div>
            <Label>Declared Total Marks</Label>
            <Input type="number" value={meta.total_marks}
                   onChange={e => setMeta(m => ({ ...m, total_marks: e.target.value }))}
                   data-testid="paper-total-marks" />
          </div>
          <div className="md:col-span-3">
            <Label>General Instructions</Label>
            <Textarea rows={4} value={meta.general_instructions}
                      onChange={e => setMeta(m => ({ ...m, general_instructions: e.target.value }))}
                      data-testid="paper-instructions" />
          </div>
          <div>
            <Label>Release Date/Time (optional)</Label>
            <Input type="datetime-local"
                   value={meta.release_at ? meta.release_at.slice(0, 16) : ''}
                   onChange={e => setMeta(m => ({ ...m, release_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                   data-testid="paper-release-at" />
            <p className="text-xs text-muted-foreground mt-1">PDF locked until this time</p>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={meta.internal_choice}
                        onCheckedChange={(v) => setMeta(m => ({ ...m, internal_choice: !!v }))}
                        data-testid="paper-internal-choice" />
              Internal choice allowed
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sections & Questions</CardTitle>
          <Button onClick={addSection} variant="outline" size="sm" data-testid="add-section-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Section
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {sections.map((sec, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3 bg-muted/30" data-testid={`section-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label className="text-xs">Section Name</Label>
                  <Input value={sec.name} onChange={e => updateSection(i, { name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Marks / Q</Label>
                  <Input type="number" step="0.5" value={sec.marks_per_question}
                         onChange={e => updateSection(i, { marks_per_question: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Section Instruction</Label>
                  <Input value={sec.instruction} onChange={e => updateSection(i, { instruction: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setPickerOpen(pickerOpen === i ? null : i)}
                          data-testid={`pick-questions-${i}`}>
                    <BookOpen className="h-4 w-4 mr-1" />
                    {pickerOpen === i ? 'Close' : 'Pick'}
                  </Button>
                  {sections.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeSection(i)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Selected questions */}
              <div className="text-sm">
                <p className="font-medium mb-2">
                  {sec.question_ids.length} question{sec.question_ids.length === 1 ? '' : 's'} selected ·
                  Total {(sec.question_ids.length * Number(sec.marks_per_question || 0)).toFixed(1)} marks
                </p>
                {sec.question_ids.length === 0 && (
                  <p className="text-muted-foreground text-xs">Click &ldquo;Pick&rdquo; to add questions from the bank.</p>
                )}
                <ol className="list-decimal pl-5 space-y-1">
                  {sec.question_ids.map(qid => {
                    const q = bankById[qid];
                    if (!q) return <li key={qid} className="text-muted-foreground text-xs">Question {qid.slice(0, 6)}…</li>;
                    return (
                      <li key={qid} className="flex items-start gap-2">
                        <span className="flex-1 truncate">{q.question_text}</span>
                        <Badge variant="outline" className="text-xs">{q.question_type}</Badge>
                        <button onClick={() => toggleQuestion(i, qid)}
                                className="text-red-500 hover:text-red-700 text-xs">remove</button>
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Picker */}
              {pickerOpen === i && (
                <div className="border-t pt-3 mt-3 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Select value={pickerFilter.type}
                            onValueChange={(v) => setPickerFilter(f => ({ ...f, type: v }))}>
                      <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        {['MCQ', 'Short', 'Long', 'Case Study', 'True/False', 'Fill in the Blank']
                          .map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={pickerFilter.difficulty}
                            onValueChange={(v) => setPickerFilter(f => ({ ...f, difficulty: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All difficulties</SelectItem>
                        {['Easy', 'Medium', 'Hard'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="search..." value={pickerFilter.search}
                           onChange={e => setPickerFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  {bankLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : visibleBank.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No questions in bank for this class & subject. Add some in Question Bank first.
                    </p>
                  ) : (
                    <div className="max-h-72 overflow-y-auto border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Question</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Marks</TableHead>
                            <TableHead>Difficulty</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleBank.map(q => (
                            <TableRow key={q.id} className="cursor-pointer"
                                      onClick={() => toggleQuestion(i, q.id)}>
                              <TableCell>
                                <Checkbox checked={sec.question_ids.includes(q.id)} />
                              </TableCell>
                              <TableCell className="max-w-md truncate">{q.question_text}</TableCell>
                              <TableCell>{q.question_type}</TableCell>
                              <TableCell>{q.marks}</TableCell>
                              <TableCell><Badge variant="outline">{q.difficulty}</Badge></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {(!meta.class_id || !meta.subject_id) && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="pt-6 flex items-center gap-3 text-sm">
            <FileCheck className="h-5 w-5 text-amber-600" />
            Select <b>Class</b> and <b>Subject</b> above to load the question bank for question selection.
          </CardContent>
        </Card>
      )}

      <AIGeneratePaperDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        classes={classes}
        subjects={subjects}
        defaultClassId={meta.class_id}
        defaultSubjectId={meta.subject_id}
        onApply={handleAiPaper}
      />
    </div>
  );
};

export default CreatePaperPage;
